import React, { useCallback } from 'react';
import { Button } from 'antd';
import { PlusOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import VariantTemplateSelector from './VariantTemplateSelector';
import VariantGroupCard from './VariantGroupCard';
import VariantEmptyState from './VariantEmptyState';
import { recalculateOptionsPricing } from '../../util/variantUtils';

const VariantConfigurationEngine = ({ value = [], onChange, categoryName }) => {
    
    // Internal updater that also triggers pricing recalculation
    const triggerChange = useCallback((newOptions) => {
        const { newOptions: processedOptions } = recalculateOptionsPricing(newOptions);
        if (onChange) {
            onChange(processedOptions);
        }
    }, [onChange]);

    const handleAddCustomGroup = () => {
        const newGroup = {
            name: '',
            autoCalculate: true,
            variants: []
        };
        triggerChange([...value, newGroup]);
    };

    const handleAddTemplateGroup = (templateName) => {
        const newGroup = {
            name: templateName,
            autoCalculate: true,
            variants: []
        };
        triggerChange([...value, newGroup]);
    };

    const handleUpdateGroup = (groupIndex, updatedGroup) => {
        const newOptions = [...value];
        newOptions[groupIndex] = updatedGroup;
        triggerChange(newOptions);
    };

    const handleDeleteGroup = (groupIndex) => {
        const newOptions = [...value];
        newOptions.splice(groupIndex, 1);
        triggerChange(newOptions);
    };

    const activeGroupNames = value.map(g => g.name).filter(Boolean);

    return (
        <div>
            {/* Header Info */}
            <div style={{ padding: '0 4px 16px', borderBottom: '1px solid #f1f5f9', marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AppstoreAddOutlined style={{ color: '#4776E6' }} /> 
                    Variant Configuration Engine
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '4px' }}>
                    Create custom groups (e.g. Size, Color, Weight) to generate product variations.
                </div>
            </div>

            {/* Template Selector */}
            <VariantTemplateSelector 
                categoryName={categoryName} 
                onSelectTemplate={handleAddTemplateGroup} 
                activeGroupNames={activeGroupNames}
            />

            {/* Group Cards */}
            {value.length === 0 ? (
                <VariantEmptyState onAdd={handleAddCustomGroup} />
            ) : (
                value.map((group, idx) => (
                    <VariantGroupCard 
                        key={idx}
                        group={group}
                        groupIndex={idx}
                        onUpdate={(updatedGroup) => handleUpdateGroup(idx, updatedGroup)}
                        onDelete={() => handleDeleteGroup(idx)}
                    />
                ))
            )}

            {/* Add Custom Group Button */}
            {value.length > 0 && (
                <Button 
                    type="dashed" 
                    onClick={handleAddCustomGroup} 
                    block 
                    icon={<PlusOutlined />}
                    style={{ 
                        height: '48px', 
                        border: '1.5px dashed #3b82f6', 
                        borderRadius: '12px', 
                        color: '#3b82f6', 
                        fontWeight: 800, 
                        fontSize: '13px',
                        background: 'transparent'
                    }}
                >
                    Add Custom Variant Group
                </Button>
            )}
        </div>
    );
};

export default React.memo(VariantConfigurationEngine);
